import React from 'react'
import Nav from '../src/components/Nav'
import { urls } from '../src/utils/config'
import Footer from '../src/components/Footer'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles } from '@material-ui/core/styles'
import Background from '../src/components/Subnav/Background'
import PostGrid from '../src/components/Grids/PostGrid'
import { Typography, Divider, Chip } from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import HelpSection from '../src/components/Help/HelpSection'
import JoinCommunitySection from '../src/components/Footer/JoinCommunitySection'
import JoinTokenCommunitySection from '../src/components/Footer/JoinTokenCommunitySection'
import { getPosts } from '../src/services/posts'
import { Post as IPost } from '../types/post'
import { GetServerSideProps } from 'next'
import Image from 'next/image'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '1200px',
    margin: 'auto',
  },
  contentSection: {
    margin: '90px 0 0',
    padding: '20px',
    [theme.breakpoints.up('sm')]: {
      margin: '150px 0 0',
    },
    [theme.breakpoints.up('md')]: {
      margin: '120px 0 0',
    },
  },
  section: {},
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '80px 0',
  },
  aboveFold: {
    minHeight: '20vh',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: -1,
  },
  title: {
    color: 'white',
    textAlign: 'left',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '48px',
      maxWidth: '100%',
      margin: '0 auto 0 auto',
    },
  },
  subtitle: {
    marginTop: '20px',
    marginBottom: '30px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  heroTextSection: {
    width: '100%',
    padding: '20px',
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
  heroImageSection: {
    display: 'none',
    width: '40%',
    height: 'inherit',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '480px',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  heroImage: {
    width: '100%',
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
  chip: {
    padding: '4px',
    width: 'fit-content',
    marginBottom: '4px',
  },
}))

interface Props {
  featuredPodcasts: IPost[]
  featuredArticles: IPost[]
  featuredReviews: IPost[]
  featuredTutorials: IPost[]
}

function Hub({
  featuredPodcasts,
  featuredArticles,
  featuredReviews,
  featuredTutorials,
}: Props): JSX.Element {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.home,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters | The #1 Crypto Product Comparison Platform`}
            description={`Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`}
            openGraph={{
              url: `https://${process.env.NEXT_PUBLIC_APP_URL}/hub`,
              title: `Cryptotesters | The #1 Crypto Product Comparison Platform`,
              description: `Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/hub.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/hub.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <div className={classes.aboveFold}>
              <Background size="xl" />
              <div className={classes.heroContent}>
                <div className={classes.heroTextSection}>
                  <Typography variant="h1" className={classes.title}>
                    Be up to date, <span className={classes.purpleText}>with our Crypto hub</span>.
                  </Typography>
                  <Typography variant="subtitle2" className={classes.subtitle}>
                    Stay informed about the crypto world with our podcasts & reviews. Or just become
                    part of our community to get the deepest insight.
                  </Typography>
                </div>
                <div className={classes.heroImageSection}>
                  <Image
                    layout="fill"
                    objectFit="contain"
                    src={'/hub.png'}
                    className={classes.heroImage}
                  />
                </div>
              </div>
            </div>
            <div className={classes.contentSection}>
              {featuredArticles.length > 0 && (
                <div>
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Featured Articles </Typography>
                    <Chip
                      label="See all articles"
                      className={classes.chip}
                      clickable
                      href="/blog"
                      component="a"
                      onDelete={() => {
                        window.location.href = '/blog'
                      }}
                      deleteIcon={<ArrowForwardIcon />}
                    />
                  </div>
                  <PostGrid posts={featuredArticles} type="blog" />
                </div>
              )}

              {featuredReviews.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Featured Reviews </Typography>
                    <Chip
                      label="See all reviews"
                      className={classes.chip}
                      clickable
                      href="/review"
                      component="a"
                      onDelete={() => {
                        window.location.href = '/review'
                      }}
                      deleteIcon={<ArrowForwardIcon />}
                    />
                  </div>
                  <PostGrid posts={featuredReviews} type="review" />
                </div>
              )}

              {featuredTutorials.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Featured Tutorials </Typography>
                    <Chip
                      label="See all tutorials"
                      className={classes.chip}
                      clickable
                      href="/tutorial"
                      component="a"
                      onDelete={() => {
                        window.location.href = '/tutorial'
                      }}
                      deleteIcon={<ArrowForwardIcon />}
                    />
                  </div>
                  <PostGrid posts={featuredTutorials} type="blog" />
                </div>
              )}

              {featuredPodcasts.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Featured Podcasts </Typography>
                    <Chip
                      label="See all podcasts"
                      className={classes.chip}
                      deleteIcon={<ArrowForwardIcon />}
                      clickable
                      href="/podcast"
                      component="a"
                      onDelete={() => {
                        window.location.href = '/podcast'
                      }}
                    />
                  </div>
                  <PostGrid posts={featuredPodcasts} type="podcast" />
                </div>
              )}
            </div>
          </div>
          <JoinTokenCommunitySection />
          <HelpSection />
          <JoinCommunitySection />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let featuredPodcasts: IPost[] = []
  let featuredArticles: IPost[] = []
  let featuredReviews: IPost[] = []
  let featuredTutorials: IPost[] = []

  try {
    featuredReviews = await getPosts('review', 4)
    featuredArticles = await getPosts('blog', 4)
    featuredPodcasts = await getPosts('podcast', 4)
    featuredTutorials = await getPosts('tutorial', 4)
  } catch (e) {
    console.error(e.message)
  }

  return {
    props: {
      featuredPodcasts,
      featuredArticles,
      featuredReviews,
      featuredTutorials,
    },
  }
}

export default Hub
